import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  ChevronRight,
  User,
  Shield,
  Menu,
  Settings,
  Key,
  Users,
  Lock,
  Eye,
  CheckCircle,
  AlertTriangle,
  Info,
  Plus,
  Edit,
  Trash2,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import settingsService from '../../services/settingsService'
// Consolidated CSS is imported globally'

const SecurityMaster = () => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [counts, setCounts] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await settingsService.getDashboardData()
        if (!cancelled && res?.success) {
          setCounts(res.data?.masterData || {
            users: res.data?.metrics?.totalUsers,
            roles: res.data?.metrics?.totalRoles,
            menuItems: res.data?.metrics?.totalMenuItems,
          })
        }
      } catch (_) {
        /* keep null counts */
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const labelCount = (n, singular, plural = `${singular}s`) => {
    if (loading) return '…'
    if (n == null) return '—'
    return `${n} ${n === 1 ? singular : plural}`
  }

  const handleBack = () => {
    navigate('/settings')
  }

  const handleCardClick = (cardType) => {
    console.log(`Clicked on ${cardType}`)
    // Add navigation logic for each card type
    switch (cardType) {
      case 'User':
        navigate('/settings/user-master')
        break
      case 'Role':
        navigate('/settings/role-master')
        break
      case 'Menu Item':
        navigate('/settings/menu-item-master')
        break
      case 'Module Management':
        navigate('/settings/submodule-master')
        break
      case 'Module Permissions':
        navigate('/settings/module-permissions')
        break
      default:
        console.log(`Navigation for ${cardType} not implemented`)
    }
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Masters',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Security Master',
      path: '/settings/security-master',
      isCurrent: true
    }
  ]

  const helpContent = (
    <>
      <h3>Security Master – Overview</h3>
      <p>Security Master defines users, roles, menu structure, and module-level permissions. Configure these to control who can access which parts of the ERP.</p>

      <h4>User</h4>
      <p><strong>Purpose:</strong> Create and manage user accounts that log in to the system.</p>
      <p><strong>Details:</strong> The User module holds login credentials, name, email, and role assignment. Add, edit, or deactivate users here. Each user must be assigned at least one role. Use the list to search users and reset passwords or update profile details.</p>

      <h4>Role</h4>
      <p><strong>Purpose:</strong> Define roles and assign them to users so permissions can be managed by role instead of per user.</p>
      <p><strong>Details:</strong> Roles (e.g. Admin, Manager, Viewer) group a set of permissions. Create roles in the Role master and assign users to roles in the User master. Module Permissions then control what each role can access. This keeps security manageable as the user base grows.</p>

      <h4>Menu Item</h4>
      <p><strong>Purpose:</strong> Configure the application menu: which items appear in the sidebar and in what order.</p>
      <p><strong>Details:</strong> Menu Item master defines the navigation entries (Dashboard, Sales, Projects, etc.) and their hierarchy. You can add, reorder, or hide menu items. Visibility can be tied to permissions so that only authorised roles see certain items. Use this to customise the sidebar and sub-menus.</p>

      <h4>Module Management</h4>
      <p><strong>Purpose:</strong> Manage the modules (cards/screens) under Masters, Transactions, and Reports within each business function (e.g. Sales, Procurement).</p>
      <p><strong>Details:</strong> Each business function (Sales, Expenses, Accounts, etc.) has tabs like Masters, Transactions, Reports. The cards on those tabs are “modules”. Module Management lets you add, edit, or disable these modules—titles, display names, types, and visibility. Changes here affect what appears on the corresponding tab and can be used with Module Permissions.</p>

      <h4>Module Permissions</h4>
      <p><strong>Purpose:</strong> Set which roles can access which modules within each business function.</p>
      <p><strong>Details:</strong> Module Permissions maps roles to modules: for each business function and its modules (from Module Management), you grant or deny access by role. Users inherit access through their assigned role. Use this to restrict sensitive areas (e.g. Settings, financial reports) to specific roles and to hide modules from others.</p>

      <h3>How to use</h3>
      <p>Click any card to open that module’s list or configuration screen. Set up <strong>Roles</strong> first, then <strong>Users</strong> (assigning roles). Use <strong>Menu Item</strong> and <strong>Module Management</strong> to define structure; use <strong>Module Permissions</strong> to control access by role. Typically: create roles → create users and assign roles → configure menu and modules → set permissions per role.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Security Master</h1>
          <p>Manage user security, roles, and permissions</p>
        </div>
      </div>

      {/* Security Cards Grid */}
      <div className="settings-masters-grid">
        {/* User Card */}
        <div className="settings-master-card" onClick={() => handleCardClick('User')}>
          <div className="settings-master-icon">
            <User size={24} />
          </div>
          <div className="settings-master-content">
            <h3>User</h3>
            <p>User management and accounts</p>
            <div className="settings-master-count">{labelCount(counts?.users, 'User')}</div>
          </div>
        </div>

        {/* Role Card */}
        <div className="settings-master-card" onClick={() => handleCardClick('Role')}>
          <div className="settings-master-icon">
            <Shield size={24} />
          </div>
          <div className="settings-master-content">
            <h3>Role</h3>
            <p>Role definitions and assignments</p>
            <div className="settings-master-count">{labelCount(counts?.roles, 'Role')}</div>
          </div>
        </div>

        {/* Menu Item Card */}
        <div className="settings-master-card" onClick={() => handleCardClick('Menu Item')}>
          <div className="settings-master-icon">
            <Menu size={24} />
          </div>
          <div className="settings-master-content">
            <h3>Menu Item</h3>
            <p>Menu configuration and items</p>
            <div className="settings-master-count">{labelCount(counts?.menuItems, 'Item')}</div>
          </div>
        </div>

        {/* Module Management Card - Modules are cards under Masters / Transactions / Reports per Business Function */}
        <div className="settings-master-card" onClick={() => handleCardClick('Module Management')}>
          <div className="settings-master-icon">
            <Settings size={24} />
          </div>
          <div className="settings-master-content">
            <h3>Module Management</h3>
            <p>Manage modules under Masters, Transactions and Reports</p>
            <div className="settings-master-count">Modules</div>
          </div>
        </div>

        {/* Module Permissions Card */}
        <div className="settings-master-card" onClick={() => handleCardClick('Module Permissions')}>
          <div className="settings-master-icon">
            <Key size={24} />
          </div>
          <div className="settings-master-content">
            <h3>Module Permissions</h3>
            <p>Permission settings for modules by role and business function</p>
            <div className="settings-master-count">Permissions</div>
          </div>
        </div>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Security Master – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default SecurityMaster
